import {
    AppBar,
    Badge,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {Link as RouterLink, useLocation, useNavigate} from "@tanstack/react-router";
import {
    Briefcase,
    ClipboardCheck,
    ClipboardList,
    FileText,
    Info,
    LogOut,
    Menu as MenuIcon,
    ShoppingCart,
    User,
    UserPlus,
    Users,
    X,
} from "lucide-react";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import CartTimeoutBanner from "@/components/CartTimeoutBanner.tsx";
import InstructorInstructionsModal from "@/components/Modal/InstructorInstructionsModal.tsx";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import {
    endServerSession,
    type JWTInstructor,
    type JWTUser,
    useSignOutInstructor,
    useUser,
} from "@/components/Providers/JWTProvider.tsx";
import {useMembershipReportAccess} from "@/components/Providers/MembershipReportAccessProvider.tsx";
import {useStripeTerminal} from "@/components/Providers/StripeTerminalProvider.tsx";
import LogoImage from "@/images/Glenbrook-HS_RGB_rev.svg";

type MenuItem = {
    icon: React.ReactElement;
    label: string;
    addDivider: boolean;
    path?: string;
    onClick?: () => void;
};

const buildEmployeeMenuItems = (
    user: JWTInstructor,
    hasBoosterAccess: boolean,
    onShowInstructions: () => void,
): MenuItem[] => {
    const items: MenuItem[] = [
        {
            icon: <FileText size={22} />,
            label: "FEES",
            path: "/instructor/fees",
            addDivider: false,
        },
    ];

    if (user.role.includes("fee_admin")) {
        items.push({
            icon: <ClipboardList size={22} />,
            label: "MY FEE REQUESTS",
            path: "/instructor/fee-proposals",
            addDivider: true,
        });
    }

    if (user.role.includes("instructor")) {
        items.push({
            icon: <User size={22} />,
            label: "My Activities",
            path: "/instructor",
            addDivider: false,
        });
    }

    if (hasBoosterAccess) {
        items.push({
            icon: <Users size={22} />,
            label: "BOOSTERS",
            path: "/instructor/boosters",
            addDivider: false,
        });
    }

    items.push({
        icon: <Info size={22} />,
        label: "INSTRUCTIONS",
        addDivider: true,
        onClick: onShowInstructions,
    });

    return items;
};

const buildStudentMenuItems = (user: JWTUser): MenuItem[] => {
    const items: MenuItem[] = [
        {
            icon: <FileText size={22} />,
            label: "FEES",
            path: "/fees",
            addDivider: true,
        },
    ];

    if (user.userType !== "guest_user") {
        items.push({
            icon: <UserPlus size={22} />,
            label: "REGISTER",
            path: "/programs",
            addDivider: false,
        });
        items.push({
            icon: <ClipboardCheck size={22} />,
            label: "MY REGISTRATIONS",
            path: "/my-registrations",
            addDivider: true,
        });
    }

    if (user.userType === "community_member") {
        items.push({
            icon: <UserPlus size={22} />,
            label: "My Profile",
            path: "/edit-profile",
            addDivider: false,
        });
    }

    if (user.userType !== "guest_user") {
        items.push({
            icon: <Briefcase size={22} />,
            label: "Purchases",
            path: "/cart-history",
            addDivider: false,
        });
        items.push({
            icon: <Info size={22} />,
            label: "INSTRUCTIONS",
            path: "/instructions",
            addDivider: false,
        });
        items.push({
            icon: <FileText size={22} />,
            label: "TAX STATEMENT",
            path: "/tax-statement",
            addDivider: true,
        });
    }

    return items;
};

const DRAWER_BREAKPOINT_PX = 1180;

export const LoggedInHeader: React.FC = () => {
    const {cart} = useCartContext();
    const cartItemCount = cart?.cartItems.length ?? 0;
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(DRAWER_BREAKPOINT_PX));
    const {inFileMaker} = useStripeTerminal();
    const navigate = useNavigate();
    const location = useLocation();
    const signOutInstructor = useSignOutInstructor();
    const {user, isEmployee, isStudentOrCM} = useUser();
    const {access: membershipReportAccess} = useMembershipReportAccess();
    const [showInstructorInstructionsModal, setShowInstructorInstructionsModal] = useState(false);
    const signOut = useCallback(() => {
        void endServerSession(user?.accessToken);

        if (isEmployee) {
            signOutInstructor();
            void navigate({to: "/instructor/sign-in"});
            return;
        }

        void navigate({to: "/signout"});
    }, [user, signOutInstructor, isEmployee, navigate]);

    const menuItems = useMemo(() => {
        if (isEmployee) {
            return buildEmployeeMenuItems(user, membershipReportAccess.hasAccess, () =>
                setShowInstructorInstructionsModal(true),
            );
        }

        if (isStudentOrCM) {
            return buildStudentMenuItems(user);
        }

        return [];
    }, [user, isEmployee, isStudentOrCM, membershipReportAccess.hasAccess]);

    useEffect(() => {
        if (!isMobile) {
            setMobileMenuOpen(false);
        }
    }, [isMobile]);

    const renderMobileMenu = () => (
        <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{
                sx: {
                    width: "80%",
                    maxWidth: 360,
                    bgcolor: "primary.main",
                    color: "white",
                },
            }}
        >
            <Box sx={{p: 2, display: "flex", justifyContent: "flex-end"}}>
                <IconButton
                    aria-label="Close menu"
                    onClick={() => setMobileMenuOpen(false)}
                    sx={{color: "white"}}
                >
                    <X size={24} />
                </IconButton>
            </Box>
            <List>
                {menuItems.map((item, index) => {
                    return (
                        <React.Fragment key={item.path ?? item.label}>
                            <ListItem
                                component={RouterLink}
                                to={item.path}
                                onClick={() => {
                                    item.onClick?.();
                                    setMobileMenuOpen(false);
                                }}
                                sx={{
                                    py: 2,
                                    "&:hover": {
                                        bgcolor: "rgba(255, 255, 255, 0.1)",
                                    },
                                }}
                            >
                                <ListItemIcon sx={{color: "white", minWidth: 40}}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    slotProps={{
                                        primary: {
                                            sx: {fontWeight: 500, color: "white"},
                                        },
                                    }}
                                />
                            </ListItem>
                            {index < menuItems.length - 1 && (
                                <Divider sx={{bgcolor: "rgba(255, 255, 255, 0.2)"}} />
                            )}
                        </React.Fragment>
                    );
                })}
                <Box sx={{p: 2, mt: 2}}>
                    <Button
                        component={RouterLink}
                        to={isEmployee ? "/instructor/cart" : "/cart"}
                        variant="contained"
                        fullWidth
                        startIcon={
                            <Badge
                                badgeContent={cartItemCount}
                                color="secondary"
                                sx={{
                                    "& .MuiBadge-badge": {
                                        bgcolor: "secondary",
                                        color: "white",
                                        fontWeight: "bold",
                                    },
                                }}
                            >
                                <ShoppingCart size={22} />
                            </Badge>
                        }
                        sx={{
                            bgcolor: "rgba(255, 255, 255, 0.15)",
                            color: "white",
                            py: 1.5,
                            "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.25)",
                            },
                        }}
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        CART
                    </Button>
                </Box>
                {!inFileMaker && (
                    <Box sx={{p: 2}}>
                        <Button
                            fullWidth
                            startIcon={<LogOut size={22} />}
                            onClick={signOut}
                            sx={{
                                color: "white",
                                py: 1.5,
                                border: "1px solid rgba(255, 255, 255, 0.3)",
                                "&:hover": {
                                    border: "1px solid rgba(255, 255, 255, 0.5)",
                                    bgcolor: "rgba(255, 255, 255, 0.1)",
                                },
                            }}
                        >
                            SIGN OUT
                        </Button>
                    </Box>
                )}
            </List>
        </Drawer>
    );

    return (
        <>
            <AppBar position="static" color={"primary"}>
                <Toolbar sx={{justifyContent: "space-between"}}>
                    <RouterLink
                        to="/"
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src={LogoImage}
                            alt="Glenbrook High School"
                            sx={{
                                width: isMobile ? "100px" : "130px",
                                height: "auto",
                                margin: "5px",
                            }}
                        />
                    </RouterLink>

                    {isMobile ? (
                        <Box sx={{display: "flex", gap: 2}}>
                            <IconButton
                                aria-label={`Cart, ${cartItemCount} item${cartItemCount === 1 ? "" : "s"}`}
                                component={RouterLink}
                                to={isEmployee ? "/instructor/cart" : "/cart"}
                                sx={{color: "white"}}
                            >
                                <Badge
                                    badgeContent={cartItemCount}
                                    color="secondary"
                                    sx={{
                                        "& .MuiBadge-badge": {
                                            bgcolor: "secondary",
                                            color: "white",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    <ShoppingCart size={24} />
                                </Badge>
                            </IconButton>
                            <IconButton
                                aria-label="Open menu"
                                onClick={() => setMobileMenuOpen(true)}
                                sx={{color: "white"}}
                            >
                                <MenuIcon size={24} />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box sx={{display: "flex", gap: 1.5, alignItems: "center"}}>
                            {menuItems.map(item => {
                                return (
                                    <React.Fragment key={item.path ?? item.label}>
                                        <Button
                                            component={RouterLink}
                                            to={item.path}
                                            onClick={() => {
                                                if (item.onClick) {
                                                    item.onClick();
                                                }
                                            }}
                                            sx={{
                                                color: "inherit",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 1,
                                                minWidth: "auto",
                                                px: 1,
                                                borderRadius: 2,
                                                py: 1,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    bgcolor: "rgba(255, 255, 255, 0.25)",
                                                },
                                            }}
                                        >
                                            {item.icon}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    lineHeight: 1,
                                                    fontSize: "0.88rem",
                                                    textAlign: "center",
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Button>
                                        {item.addDivider ? (
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                                sx={{
                                                    bgcolor: "rgba(255, 255, 255, 0.7)",
                                                    mx: 0.5,
                                                    width: "2px",
                                                }}
                                            />
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                            <Button
                                component={RouterLink}
                                to={isEmployee ? "/instructor/cart" : "/cart"}
                                sx={{
                                    color: "inherit",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1,
                                    minWidth: "auto",
                                    bgcolor:
                                        location.pathname ===
                                        (isEmployee ? "/instructor/cart" : "/cart")
                                            ? "rgba(255, 255, 255, 0.15)"
                                            : "transparent",
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    ml: 0.5,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "rgba(255, 255, 255, 0.25)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <Badge
                                    badgeContent={cartItemCount}
                                    color="secondary"
                                    sx={{
                                        "& .MuiBadge-badge": {
                                            bgcolor: "secondary",
                                            color: "white",
                                            fontWeight: "bold",
                                        },
                                    }}
                                >
                                    <ShoppingCart size={24} />
                                </Badge>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        lineHeight: 1,
                                        fontSize: "0.88rem",
                                        fontWeight: "bold",
                                    }}
                                >
                                    CART
                                </Typography>
                            </Button>
                            {!inFileMaker && (
                                <Button
                                    onClick={signOut}
                                    sx={{
                                        color: "inherit",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1,
                                        minWidth: "auto",
                                        px: 1,
                                    }}
                                >
                                    <LogOut size={22} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            lineHeight: 1,
                                            color: "inherit",
                                            fontSize: "0.88rem",
                                        }}
                                    >
                                        SIGN OUT
                                    </Typography>
                                </Button>
                            )}
                        </Box>
                    )}
                </Toolbar>
                {renderMobileMenu()}
            </AppBar>
            {user && <CartTimeoutBanner />}
            <InstructorInstructionsModal
                show={showInstructorInstructionsModal}
                handleClose={() => setShowInstructorInstructionsModal(false)}
            />
        </>
    );
};
